// React & Libraries
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

// Ant Design
import {
  Drawer
} from 'antd';

// UI Libraries
import H3Text from '@Uilib/h3Text';

// Constants and Helpers
import Constants, { QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';

// Actions
import PaymentOutgoingActions from '@Actions/paymentOutgoingActions';
import InvoiceActions from '@Actions/invoiceActions';
import EinvoiceEwaybillIntegrationActions from '@Actions/integrations/einvoiceEwaybillActions';
import AttachmentActions from '@Actions/attachmentActions';
import UserActions from '@Actions/userActions';
import ZohoIntegrationActions from '@Actions/integrations/zohoIntegrationActions';
import UnicommerceIntegrationActions from '@Actions/integrations/unicommerceIntegrationActions';
import ShopifyIntegrationActions from '@Actions/integrations/shopifyIntegrationActions';
import TallyIntegrationActions from '@Actions/integrations/tallyIntegrationActions';
import AnalyticsActions from '@Actions/application/analyticsActions';
import ActivityLogActions from '@Actions/activityLogActions';
import BusyIntegrationActions from '../../../../actions/integrations/busyIntegrationAction';

// File specific Components
import ViewDeliveryChallan from '@Components/Inventory/DeliveryChallan/ViewDeliveryChallan';
import InvoiceLines from './InvoiceLines';
import DeliveryChallanForm from '../../../Inventory/DeliveryChallan/DeliveryChallanForm';
import CreditNoteForm from '../../CreditNote/CreditNoteForm';
import ViewCreditNote from '../../CreditNote/ViewCreditNote';
import './style.scss';
import ViewIncomingPayment from '@Components/Sales/IncomingPayments/ViewIncomingPayment';
import 'react-quill/dist/quill.bubble.css';
import SendWhatsapp from '@Components/Common/ViewPurchaseOrder/SendWhatsapp';
import RecordIncomingPayment from '../../../Payments/RecordPayment/RecordIncomingPayment';
import LinkIncomingPayment from '../../../Payments/LinkPayment/LinkIncomingPayment';
import ViewInvoiceLeftSectionHeader from './ViewInvoiceLeftSectionHeader';
import ViewInvoiceDetails from './ViewInvoiceDetails';
import ViewInvoiceAddresses from './ViewInvoiceAddresses';
import ViewInvoiceFooter from './ViewInvoiceFooter';
import IssueStockForInvoice from './IssueStockForInvoice';
import { INITIAL_STATE, HideAdhocApprovalsHelper, getLinkedDocumentInfoHelper, getTotalQuantity } from './helper';
import EwaybillForm from '../../EinvoicingEwaybill/EwaybillList/EwaybillForm';

// Common Components
import AddressSelector from '@Components/Common/FormUtils/AddressSelecter';
import MultiDocumentDownload from '@Components/Common/MultiDocumentDownload';
import DownloadLabels from '../../../Common/DownloadLabels';
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import ViewDocRightSection from '@Components/Common/ViewDocRightSection';
import PRZDrawer from '@Components/Common/UI/PRZDrawer';

function ViewInvoice(props) {

  const {
    getInvoiceById, match, user, isQuickView, selectedTenantId, selectedInvId, selectedInvoice, getInvoiceByIdSuccess, updateInvoiceStatusLoading, updateInvoiceWorkflowStepLoading, updateInvoiceWorkflowStep, getInvoiceByIdLoading, pushInvoiceToBusyLoading, statusCallback, getPaymentsOutgoingSuccess, MONEY, quickUpdateInvoice, getActivityLog
  } = props;

  const [state, setState] = useState(INITIAL_STATE);

  const {
    isUserReady, linkPayment, showPayment, showEWaybillFormDrawer, dataForIssueStock, allStock, dataForViewInvoiceLines, updatedGstNumber, isModalOpen, includeEWayBill, eWayBillType, isGenerateLabelModalOpen, visibleColumns, downloadDocumentInBaseCurrency, downloadInvoiceWithTaxReport, isHsnEditable, isUserReadyForIssueStock, showCNForm, openCNModalView, showDCDrawer, openDCModalView, selectedDC, selectedCreditNote, showWhatsappDrawer, showStockIssueDrawer, currentInvId, currentTenantId, showViewPayout, selectedPayment, selectedTags, showAddressDrawer, selectedAddressType, cfInvoiceLine
  } = state;

  const updateState = (newState) => {
    setState((oldState) => ({
      ...oldState,
      ...newState,
    }));
  };

  useEffect(() => {

    const { invoiceId } = match.params;
    getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId, (invoiceData) => {
      updateState({
        fileList: invoiceData?.attachments,
        selectedTags: invoiceData?.tags?.length > 0 ? invoiceData?.tags : [],
        dataForViewInvoiceLines: invoiceData?.invoice_lines,
      });
    });

    return () => {
      getInvoiceByIdSuccess(null);
    };
  }, []);

  useEffect(() => {

    const { invoiceId } = match.params;

    if (selectedInvoice && !isHsnEditable) {
      const lineCf = selectedInvoice?.invoice_lines?.[0]?.invoice_line_custom_fields;
      updateState({
        isHsnEditable: true,
        dataForViewInvoiceLines: selectedInvoice?.invoice_lines,
        updatedGstNumber: selectedInvoice?.gst_number || selectedInvoice?.customer_info?.gst_number,
        cfInvoiceLine: lineCf,
        visibleColumns: CustomFieldHelpers.updateVisibleColumns(lineCf, state.visibleColumns),
      });
    }

    if (user?.tenant_info?.tenant_id && !isUserReady) {
      getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
      updateState({
        isUserReady: true,
        currentInvId: isQuickView ? selectedInvId : invoiceId,
        currentTenantId: isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id),
      });
    }

    if (selectedInvoice && !isUserReadyForIssueStock) {

      const selectedInvoiceWithUniqueKey = {
        ...selectedInvoice, // Preserve the rest of the properties in selectedInvoice
        invoice_lines: selectedInvoice?.invoice_lines?.map((item) => ({
          ...item,
          all_product_batches: item?.all_product_batches?.map((batchData) => ({
            ...batchData,
            key: uuidv4(), // Generate a unique key for each batch
          })),
        })),
      };
      // Prepare data for Issue Stock
      const preFillingBatchQty = selectedInvoiceWithUniqueKey?.invoice_lines?.map((item) => {
        if (!item?.bundle_products.length > 0) {
          let itemQuantity = (item?.quantity + item?.free_quantity) - item?.invoice_line_adjustment_issued_quantity; // Store the original item quantity for clarity and to avoid mutating the original item directly in the loop.

          return item.all_product_batches?.reduce((acc, batch) => {
            if (!batch.is_rejected_batch && itemQuantity > 0) { // Check if batch is not rejected
              const availableQty = batch?.available_qty; // Available quantity in the current batch
              const consumed_qty = Math.min(itemQuantity, availableQty); // The quantity to consume from this batch

              // Update the batch with the consumed quantity and mark as in use
              acc.push({
                ...batch,
                batch_in_use: true,
                consumed_qty,
              });

              itemQuantity -= consumed_qty; // Reduce the remaining item quantity by the consumed amount
            }
            return acc; // Return the accumulator for the next iteration
          }, []);
        }
        // Handle the case when there are bundle products
        let itemQuantity = (item?.quantity + item?.free_quantity) - item?.invoice_line_adjustment_issued_quantity;

        return item.bundle_products?.reduce((acc, bundle) => {
          const bundleQuantity = Number(bundle?.bundle_quantity);

          return bundle.all_product_batches?.reduce((acc, batch) => {
            if (!batch.is_rejected_batch && itemQuantity > 0) { // Check if batch is not rejected
              const availableQty = batch?.available_qty;
              const consumed_qty = Math.min(itemQuantity * bundleQuantity, availableQty);

              acc.push({
                ...batch,
                batch_in_use: true,
                consumed_qty,
              });

              itemQuantity -= consumed_qty;
            }
            return acc;
          }, []);
        }, []);
      });
      const updatedInvoiceLines = selectedInvoiceWithUniqueKey?.invoice_lines?.map((line) => {
        const updatedProductBatches = line?.all_product_batches?.map((batch) => {
          const matchingBatch = preFillingBatchQty?.flat()?.find((updatedBatch) => updatedBatch?.key === batch?.key);
          if (matchingBatch) {
            return matchingBatch;
          }
          return batch;
        });
        return {
          ...line,
          all_product_batches: updatedProductBatches,
        };
      });

      updateState({
        isUserReadyForIssueStock: true,
        updatedPaymentTerms: Number(selectedInvoice?.payment_terms?.[0]?.due_days),
        updatePaymentRemark: selectedInvoice?.payment_terms?.[0]?.remark,
        updateAccountManager: selectedInvoice?.account_manager_id,
        updateDueDate: selectedInvoice?.invoice_due_date ? dayjs(selectedInvoice.invoice_due_date) : null,
        dataForViewInvoiceLines: selectedInvoice?.invoice_lines,
        dataForIssueStock: updatedInvoiceLines?.map((item) => {
          const updatedItem = {
            ...item,
            key: uuidv4(),
            issueStockQuantity: item?.quantity + item?.free_quantity,
            tenantId: selectedInvoice?.tenant_id,
            tenant_department_id: selectedInvoice?.tenant_department_id,
            batchConsumptionMethod: item?.default_outwards_method || Helpers.getBatchMethod(item),
            quantity: item?.bundle_products?.length > 0 ? '' : (item?.quantity + item?.free_quantity) - item?.invoice_line_adjustment_issued_quantity,
            product_batches: item?.all_product_batches?.map((batch) => ({
              ...batch,
              batch_in_use: true,
            })),
            manufacturingDateFormat: item?.product_sku_info?.manufacturing_date_format,
            expiryDateFormat: item?.product_sku_info?.expiry_date_format,
          };
          if (item?.bundle_products) {
            updatedItem.bundle_products = item?.bundle_products?.map((bundle) => {
              const updatedBundle = {
                ...bundle,
                so_quantity: Number(bundle?.bundle_quantity),
                key: uuidv4(),
                parentKey: updatedItem?.key,
              };
              if (bundle?.all_product_batches) {
                updatedBundle.product_batches = bundle?.all_product_batches?.map((batch) => ({
                  ...batch,
                  batch_in_use: true,
                }));
              }
              return updatedBundle;
            });
          }

          return updatedItem;
        }),
        fileList: selectedInvoice?.attachments,
        selectedTags: selectedInvoice?.tags?.length > 0 ? selectedInvoice?.tags : [],
      });
    }
  }, [selectedInvoice, user]);

  const HideAdhocApprovals = () => (
    HideAdhocApprovalsHelper({ props, state, updateState })
  );

  const getLinkedDocumentInfo = () => (
    getLinkedDocumentInfoHelper({ props, state, updateState })
  );

  const isTallyConnected = (selectedInvoice?.tally_configuration?.sales_voucher_integration) && (['CONFIRMED', 'VOID']?.includes(selectedInvoice?.status));

  const quickUpdateDisable = !selectedInvoice?.shopify_order_id && !selectedInvoice?.unicommerce_invoice_code && !selectedInvoice?.irn && !selectedInvoice?.e_way_bills && !(selectedInvoice?.status === 'VOID') && Helpers.getPermission(Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.UPDATE, user);

  const quantityPrecision = user?.tenant_info?.inventory_config?.settings?.quantity_precision;

  const enableLabelGeneration = user?.tenant_info?.global_config?.sub_modules?.label_generation?.is_active;

  const accountingGSTTransactionAsPerMaster = user?.tenant_info?.sales_config?.sub_modules?.customer?.settings?.gst_details_in_transaction === 'AS_PER_MASTER';

  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';

  const cancellationReasonOptions = [
    { label: 'Duplicate', value: 'DUPLICATE' },
    { label: 'Order Cancelled', value: 'ORDER CANCELLED' },
    { label: 'Data Entry Mistake', value: 'DATA ENTRY MISTAKE' },
    { label: 'Others', value: 'OTHERS' },
  ];

  return (
    <div className={`view-document__wrapper ${!isQuickView ? 'view-document__wrapper-page' : ''}`}>
      <MultiDocumentDownload
        downloadLink={`${Constants.Invoice}/download?invoice_id=${selectedInvoice?.invoice_id}&tenant_id=${selectedInvoice?.tenant_id}&include_eWay_bill=${includeEWayBill}&ewb_type=${eWayBillType}&download_document_in_base_currency=${downloadDocumentInBaseCurrency ? 'true' : 'false'}&required_tax_analysis_report=${downloadInvoiceWithTaxReport ? 'true' : 'false'}`}
        documentType="INVOICE"
        documentNumber={selectedInvoice?.invoice_number}
        isModalOpen={isModalOpen}
        toggleModal={() => updateState({ isModalOpen: !isModalOpen })}
      />
      <Drawer
        open={showAddressDrawer}
        onClose={() => updateState({ showAddressDrawer: false, selectedAddressType: '' })}
        width="360"
        destroyOnClose
      >
        <AddressSelector
          title={
            selectedAddressType === 'TENANT_SHIP_FROM'
              ? 'Ship From'
              : selectedAddressType === 'TENANT_SHIPPING'
                ? 'Shipping Address'
                : 'Billing Address'
          }
          addressType={selectedAddressType?.split('_')[0]}
          selectedAddressId={
            selectedAddressType === 'TENANT_SHIP_FROM'
              ? selectedInvoice?.ship_from_address_info?.address_id
              : selectedAddressType === 'TENANT_SHIPPING'
                ? selectedInvoice?.shipping_address_info?.address_id
                : selectedInvoice?.billing_address_info?.address_id
          }
          onAddressChange={(address) => {
            if (selectedAddressType === 'TENANT_SHIP_FROM') {
              quickUpdateInvoice({
                ship_from_address_id: address?.address_id,
                invoice_id: selectedInvoice.invoice_id,
              }, () => {
                updateState({ showAddressDrawer: false });
                getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
              });
            } else if (selectedAddressType === 'TENANT_SHIPPING') {
              quickUpdateInvoice({
                shipping_address_id: address?.address_id,
                invoice_id: selectedInvoice.invoice_id,
              }, () => {
                updateState({ showAddressDrawer: false });
                getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
              });
            } else {
              quickUpdateInvoice({
                billing_address_id: address?.address_id,
                invoice_id: selectedInvoice.invoice_id,
              }, () => {
                updateState({ showAddressDrawer: false });
                getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
              });
            }
          }}
          entityId={selectedAddressType === 'TENANT_SHIP_FROM' ? selectedInvoice?.tenant_id : selectedInvoice?.customer_id}
          entityType={selectedAddressType === 'TENANT_SHIP_FROM' ? 'TENANT' : 'CUSTOMER'}
          tenantId={selectedInvoice?.tenant_id}
        />
      </Drawer>
      {(!getInvoiceByIdLoading && !updateInvoiceStatusLoading && !pushInvoiceToBusyLoading)
        ? (
          <div className="ant-row">
            <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
              <div className={`view-left__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                <PRZDrawer
                  open={showPayment}
                  width={420}
                  headerWidth={375}
                  title="Record New Payment"
                  onClose={() => updateState({ showPayment: false })}
                  destroyOnClose
                >
                  {/* <ListUnusedPaymentIncoming
                    selectedInvoice={selectedInvoice}
                    callback={() => {
                      updateState({ showPayment: false });
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                  /> */}
                  <RecordIncomingPayment
                    selectedInvoice={selectedInvoice}
                    callback={() => {
                      updateState({ showPayment: false });
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                  />
                </PRZDrawer>
                <PRZDrawer
                  open={linkPayment}
                  width={820}
                  headerWidth={775}
                  title="Apply Credits"
                  onClose={() => updateState({ linkPayment: false })}
                  destroyOnClose
                >
                  {/* <LinkPaymentIncoming
                    selectedInvoice={selectedInvoice}
                    callback={() => {
                      updateState({ linkPayment: false });
                      getPaymentsOutgoingSuccess(null);
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                  /> */}
                  <LinkIncomingPayment
                    selectedInvoice={selectedInvoice}
                    callback={() => {
                      updateState({ linkPayment: false });
                      getPaymentsOutgoingSuccess(null);
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                  />
                </PRZDrawer>
                <PRZDrawer
                  open={showViewPayout}
                  onClose={() => updateState({ showViewPayout: false })}
                  width={680}
                  headerWidth={725}
                  title={`View Payment #${selectedPayment?.documentId}`}
                  destroyOnClose
                >
                  <ViewIncomingPayment
                    selectedPaymentId={selectedPayment?.payment_id}
                    paymentType={selectedPayment?.applied_payment_type}
                    onClose={() => {
                      updateState({ showViewPayout: false });
                    }}
                    callback={() => {
                      updateState({ showViewPayout: false });
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                  />
                </PRZDrawer>
                <ViewInvoiceLeftSectionHeader
                  props={props}
                  state={state}
                  updateState={updateState}
                  enableLabelGeneration={enableLabelGeneration}
                  isTallyConnected={isTallyConnected}
                />
                <ViewInvoiceDetails
                  props={props}
                  state={state}
                  updateState={updateState}
                  quickUpdateDisable={quickUpdateDisable}
                  isTallyConnected={isTallyConnected}
                  accountingGSTTransactionAsPerMaster={accountingGSTTransactionAsPerMaster}
                  cancellationReasonOptions={cancellationReasonOptions}
                />
                {/* Addresses */}
                <ViewInvoiceAddresses
                  props={props}
                  state={state}
                  updateState={updateState}
                  quickUpdateDisable={quickUpdateDisable}
                />
                <br />
                {/* Sale Invoice Lines */}
                <div>
                  <InvoiceLines
                    poLines={dataForViewInvoiceLines?.map((item) => ({
                      ...item,
                      lineCustomFields: item?.invoice_line_custom_fields,
                      product_category_info: item?.product_sku_info?.product_category_info,
                    })) || []}
                    selectedInvoice={selectedInvoice}
                    onClick={() => updateState({ showStockIssueDrawer: true })}
                    updateData={(data) => updateState({ dataForViewInvoiceLines: data })}
                    callback={() => {
                      updateState({
                        showStockIssueDrawer: false, isUserReady: false, isUserReadyForIssueStock: false, isUserReadyForPrefillData: false,
                      });
                      getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                    }}
                    isHsnEditable={() => updateState({ isHsnEditable: false })}
                    cfInvoiceLine={cfInvoiceLine}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={(data) => updateState({ visibleColumns: data })}
                    quickUpdateInvoiceApi={quickUpdateInvoice}
                    isEditable={quickUpdateDisable}
                    activityLogUpdater={() => getActivityLog(selectedInvoice.invoice_id, 'invoice', 1, 10)}
                  />
                  {user?.tenant_info?.sales_config?.sub_modules?.invoice?.settings?.show_total_quantity && (
                    <div className="total-quantity">
                      <span className="total-quantity__label">Total Quantity :</span>
                      <span className="total-quantity__value">
                        {user?.tenant_info?.sales_config?.sub_modules?.invoice?.settings?.calculate_total_quantity_with_parent_quantity_for_bundle_products ? getTotalQuantity(selectedInvoice?.invoice_lines) : QUANTITY(selectedInvoice?.invoice_lines?.reduce((total, item) => {
                          // If the item is a bundle, ignore its own quantity and only sum the bundle products' quantities
                          if (item.is_bundle && item.bundle_products?.length) {
                            const bundleTotal = item.bundle_products.reduce((bundleTotal, bundleItem) => {
                              return bundleTotal + (bundleItem.quantity || 0); // Sum the quantity of each product inside the bundle
                            }, 0);
                            return total + bundleTotal;
                          }
                          // If not a bundle, add the item's quantity
                          return total + (item.quantity || 0);
                        }, 0), quantityPrecision)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <ViewInvoiceFooter
                  props={props}
                  state={state}
                  updateState={updateState}
                />
              </div>
            </div>
            <ViewDocRightSection
              MONEY={MONEY}
              user={user}
              isQuickView={isQuickView}
              entityType='INVOICE'
              entityId={selectedInvoice?.invoice_id}
              tagSelectorProps={{
                isEnabled: true,
                selectedTags: selectedTags,
                isMultipleTagsAllowed: true,
                tagSearchAllowed: true,
                onChange: (value) => {
                  updateState({ selectedTags: value });
                },
                placeholder: "Select Tags",
              }}
              workflowTimelineProps={{
                isEnabled: selectedInvoice?.status?.toLowerCase() !== 'void',
                workflowSteps: selectedInvoice?.workflow_steps || {},
                workflowStepId: selectedInvoice?.workflow_step_id,
                loading: updateInvoiceWorkflowStepLoading,
                updateStep: updateInvoiceWorkflowStep,
                hideCase: HideAdhocApprovals(),
                callback: () => {
                  getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
                  if (statusCallback) {
                    statusCallback();
                  }
                },
              }}
              activityLogProps={{
                isEnabled: selectedInvoice,
                entityName: 'Invoice',
                entityType: "invoice",
              }}
              linkedDocumentsInfo={getLinkedDocumentInfo()}
            />
          </div>
        )
        : (
          //  Loader
          <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
        )}

      <PRZDrawer
        onClose={() => updateState({ showCNForm: false })}
        open={showCNForm}
        width={1250}
        headerWidth={1200}
        title="Create Credit Note"
        destroyOnClose
      >
        <CreditNoteForm
          isQuickView
          selectedInvoiceForCN={selectedInvoice}
          callback={() => {
            const { invoiceId } = match.params;
            updateState({ showCNForm: false });
            getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
          }}
          cnWithoutDeductingStock={selectedInvoice?.shopify_order_id || selectedInvoice?.unicommerce_shipping_package_code}
          invoiceType={selectedInvoice?.shopify_order_id ? 'shopify' : selectedInvoice?.unicommerce_so_code ? 'unicommerce' : 'normal'}
          isCarryForward={true}
        />
      </PRZDrawer>
      <PRZDrawer
        open={showWhatsappDrawer}
        width={360}
        headerWidth={310}
        title="Share on Whatsapp"
        mask
        onClose={() => updateState({ showWhatsappDrawer: false })}
        destroyOnClose
      >
        <SendWhatsapp selectedEntity={selectedInvoice} entityName="invoice" callback={() => updateState({ showWhatsappDrawer: false })} />
      </PRZDrawer>
      <PRZDrawer
        onClose={() => updateState({ openCNModalView: false })}
        open={openCNModalView}
        width={820}
        headerWidth={775}
        title="View Credit Note"
        destroyOnClose
      >
        <ViewCreditNote
          selectedCNId={selectedCreditNote?.cn_id}
          callback={() => {
            const { invoiceId } = match.params;
            updateState({ openCNModalView: false });
            getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
          }}
          isQuickView
        />
      </PRZDrawer>
      <PRZDrawer
        onClose={() => updateState({ showDCDrawer: false })}
        open={showDCDrawer}
        width={1200}
        headerWidth={1150}
        destroyOnClose
      >
        <DeliveryChallanForm
          selectedInvoice={{
            ...selectedInvoice,
            tags: selectedTags || [],
            customer_info: {
              ...((selectedInvoice && selectedInvoice.customer_info) || {}),
              gst_number: updatedGstNumber,
            },
          }}
          callback={() => {
            const { invoiceId, tenantId } = match.params;
            updateState({ showDCDrawer: false });
            getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (tenantId || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
          }}
          documentType="Invoice"
        />
      </PRZDrawer>
      <PRZDrawer
        open={showStockIssueDrawer}
        onClose={() => updateState({ showStockIssueDrawer: false })}
        width={1020}
        headerWidth={975}
        title="Issue Stock For Invoice"
        destroyOnClose
      >
        <IssueStockForInvoice
          title={() => (
            <div className="create-invoice-title__wrapper">
              <H3Text
                text={`Invoice Products (${selectedInvoice?.invoice_lines.filter((item) => item.product_sku_name?.length > 0)?.length})`}
                className="create-po__input-row__label"
              />
            </div>
          )}
          updateData={(updatedData) => updateState({ dataForIssueStock: updatedData })}
          toggleBatch={(batch, adjustmentRow) => toggleBatch(batch, adjustmentRow)}
          toggleBatchInner={(batch, adjustmentRow) => toggleBatchInner(batch, adjustmentRow)}
          allStock={allStock}
          toggleAllStock={() => updateState({ allStock: !allStock })}
          // moRmLines={moRmLines}
          data={dataForIssueStock}
          selectedInvoice={selectedInvoice}
          callback={() => {
            updateState({
              showStockIssueDrawer: false, isUserReadyForIssueStock: false, isUserReady: false, isUserReadyForPrefillData: false,
            });
            getInvoiceById(user?.tenant_info?.org_id, currentTenantId, currentInvId);
          }}
        />
      </PRZDrawer>
      <PRZDrawer
        onClose={() => updateState({ openDCModalView: false })}
        open={openDCModalView}
        width={920}
        headerWidth={870}
        title="View Delivery Challan"
        destroyOnClose
      >
        <ViewDeliveryChallan
          selectedDcSourceTenant={selectedInvoice?.source_department_info}
          selectedDCId={selectedDC?.documentId}
          callback={() => {
            const { invoiceId, tenantId } = match.params;
            updateState({ openDCModalView: false });
            getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (tenantId || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
          }}
          selectedDocType="CHL"
          isQuickView
        />
      </PRZDrawer>
      <PRZDrawer
        open={showEWaybillFormDrawer}
        onClose={() => updateState({ showEWaybillFormDrawer: false })}
        width={520}
        headerWidth={475}
        title="Create e-Way Bill"
        destroyOnClose
      >
        <EwaybillForm
          drawermode
          selectedDocument={selectedInvoice}
          selectedDocType="INV"
          documentDate={selectedInvoice?.invoice_date}
          selectedDocumentId={selectedInvoice?.invoice_id}
          selectedDocumentNumber={selectedInvoice?.invoice_number}
          callback={() => {
            const { invoiceId } = match.params;
            getInvoiceById(user?.tenant_info?.org_id, isQuickView ? selectedTenantId : (Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.INVOICE, Helpers.permissionTypes.READ).join(',') || user?.tenant_info?.tenant_id), isQuickView ? selectedInvId : invoiceId);
            updateState({ showEWaybillFormDrawer: false });
          }}
        />
      </PRZDrawer>
      <DownloadLabels
        documentType="invoice"
        documentId={selectedInvoice?.invoice_id}
        isModalOpen={isGenerateLabelModalOpen}
        toggleModal={() => updateState({ isGenerateLabelModalOpen: !isGenerateLabelModalOpen })}
        setIsLabelAvailable={(val) => updateState({ isLabelAvailable: val })}
        selectedInvoice={selectedInvoice}
      />
    </div>
  );
};

const mapStateToProps = ({
  UserReducers, InvoiceReducers, AttachmentReducers, UnicommerceIntegrationReducers, ShopifyIntegrationReducers, ZohoIntegrationReducers, TallyIntegrationReducers, EinvoiceEwaybillReducers, BusyIntegrationReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getInvoiceByIdLoading: InvoiceReducers.getInvoiceByIdLoading,
  selectedInvoice: InvoiceReducers.selectedInvoice,
  updateInvoiceStatusLoading: InvoiceReducers.updateInvoiceStatusLoading,
  updateInvoiceWorkflowStepLoading: InvoiceReducers.updateInvoiceWorkflowStepLoading,
  deleteInvoiceLoading: InvoiceReducers.deleteInvoiceLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  syncSOLoading: UnicommerceIntegrationReducers.syncSOLoading,
  syncInvoiceLoading: ZohoIntegrationReducers.syncInvoiceLoading,
  syncShopifySOLoading: ShopifyIntegrationReducers.syncShopifySOLoading,
  syncTallyInvoiceLoading: TallyIntegrationReducers.syncTallyInvoiceLoading,
  pushIRPLoading: EinvoiceEwaybillReducers.pushIRPLoading,
  cancelIRPLoading: EinvoiceEwaybillReducers.cancelIRPLoading,
  pushInvoiceToBusyLoading: BusyIntegrationReducers.pushInvoiceToBusyLoading,
  quickUpdateInvoiceLoading: InvoiceReducers.quickUpdateInvoiceLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  updateInvoiceStatus: (payload, callback) => dispatch(InvoiceActions.updateInvoiceStatus(payload, callback)),
  updateInvoiceWorkflowStep: (payload, callback) => dispatch(InvoiceActions.updateInvoiceWorkflowStep(payload, callback)),
  getInvoiceById: (orgId, tenantId, invoiceId, callback) => dispatch(InvoiceActions.getInvoiceById(orgId, tenantId, invoiceId, callback)),
  getInvoiceByIdSuccess: (selectedInvoice) => dispatch(InvoiceActions.getInvoiceByIdSuccess(selectedInvoice)),
  deleteInvoice: (payload, callback) => dispatch(InvoiceActions.deleteInvoice(payload, callback)),
  switchBusinessUnit: (tenantId, callback) => dispatch(UserActions.switchBusinessUnit(tenantId, callback)),
  getAttachmentById: (entityId, entityName, callback) => dispatch(AttachmentActions.getAttachmentById(entityId, entityName, callback)),
  updateAttachment: (payload, callback) => dispatch(AttachmentActions.updateAttachment(payload, callback)),
  getPaymentsOutgoingSuccess: (payments) => dispatch(PaymentOutgoingActions.getPaymentsOutgoingSuccess(payments)),
  syncSO: (payload, callback) => dispatch(UnicommerceIntegrationActions.syncSO(payload, callback)),
  syncShopifySO: (payload, callback) => dispatch(ShopifyIntegrationActions.syncShopifySO(payload, callback)),
  syncInvoice: (payload, callback) => dispatch(ZohoIntegrationActions.syncInvoice(payload, callback)),
  syncTallyInvoice: (payload, callback) => dispatch(TallyIntegrationActions.syncTallyInvoice(payload, callback)),
  pushIRP: (payload, callback) => dispatch(EinvoiceEwaybillIntegrationActions.pushIRP(payload, callback)),
  cancelIRP: (payload, callback) => dispatch(EinvoiceEwaybillIntegrationActions.cancelIRP(payload, callback)),
  downloadDocument: (payload, document) => dispatch(AnalyticsActions.downloadDocument(payload, document)),
  pushInvoiceToBusy: (payload, callback) => dispatch(BusyIntegrationActions.pushInvoiceToBusy(payload, callback)),
  quickUpdateInvoice: (payload, callback, rollback) => dispatch(InvoiceActions.quickUpdateInvoice(payload, callback, rollback)),
  getActivityLog: (entityId, entityName, page, limit, callback) => dispatch(ActivityLogActions.getActivityLog(entityId, entityName, page, limit, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewInvoice));
